import { LockFilled } from '@ant-design/icons';
import { ScopeButton } from '@monorepo/shared-lib';
import { Col, Form, Row, Select, Spin } from 'antd';
import type { RangePickerProps } from 'antd/es/date-picker';
import { FormDatePicker } from 'components/Custom/datePicker';
import { LabelWrapper } from 'components/Custom/labels';
import { FormSelect } from 'components/Custom/selects';
import { StyledTooltip } from 'components/Custom/Tooltip';
import { UpgradeMessage } from 'containers/UpgradeMessage';
import dayjs from 'dayjs';
import { formatDisplayInteger } from 'i18n/arabicDigits';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCountryLabel } from 'hooks/useCountryLabel';
import { useMultiLineMaxTagCount } from 'hooks/useMultiLineMaxTagCount';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import routes from 'routes';
import { ReturnFrequency, UpgradeMessageSource } from 'store/api/enums';
import { getReturnFrequencyLabel } from 'i18n/returnFrequencyLabel';
import {
  turnDrawerOff,
  turnModalOff,
  turnModalOn,
  updateMappingData,
} from 'store/app/actions';
import { WindowType } from 'store/app/enums';
import { getMappingData } from 'store/app/selectors';
import { ModalData } from 'store/app/types';
import { getAuthUser, getAuthUserLicence } from 'store/auth/selectors';
import {
  getCurrentSubscription,
  getSubscriptionPlanTypes,
} from 'store/billing/selectors';
import { MappingData } from 'store/returns/models';
import { fetchAddReturnDataRequest } from 'store/returns/actions';
import { getReturnTypeNameLabel } from 'i18n/returnTypeLabel';
import {
  isMaltaTifdReturnType,
  maltaTifdMappingPath,
} from 'utils/tifd-mapping';
import { ReturnType } from 'store/returns/models/returnData';
import {
  getReturnData,
  getReturnLoading,
  getReturnPreLoading,
  getReturnTaxRatesLoading,
} from 'store/returns/selectors';
import styled from 'styled-components';
import { calculateNextPeriod } from 'utils/calculate-next-period';
import { buildGroupReturnContextSearch } from 'utils/group-return-context';
import { isOssOutOfScopeSectionName } from 'utils/oss-mapping';
import { suggestFirstSubmissionDeadlineAfterPeriodEnd } from 'utils/suggest-first-submission-deadline';
import { isSmallOrMediumPlanLocked } from 'utils/subscriptionPlanGate';

const DescriptionBox = styled.div.attrs({
  className: 'mapping-description-box',
})`
  font-family: inherit;
  border-radius: 5px;
  border: 1px solid var(--color-border, #dbeae3);
  opacity: 0px;
  background: var(--color-status-warning-bg, #fff5e2);
  margin: -18px 0 20px 0;
  padding: 10px 15px;
  font-size: 11px;
  line-height: 13px;
  text-align: left;
  min-height: 45px;
  color: var(--color-text-base, #2e2e2e);
`;

const PeriodBox = styled.div.attrs({
  className: 'mapping-period-box',
})`
  font-family: inherit;
  height: 45px;
  border: 1px solid var(--color-border, #dbeae3);
  color: var(--color-text-dark, #042a2a);
  font-weight: 600;
  background: var(--color-surface, #f3f7f8);
  padding: 10px 15px;
`;

const NoteText = styled.span.attrs({
  className: 'mapping-note-text',
})`
  font-family: inherit;
  font-size: 11px;
  font-weight: 400;
  line-height: 18px;
  text-align: left;
  color: var(--color-text-base, #2e2e2e);
`;

const OssCountrySelect = styled(FormSelect)`
  .ant-select-selector {
    min-height: 45px !important;
    height: auto !important;
    display: flex !important;
    align-items: flex-start !important;
    padding-top: 4px !important;
    padding-bottom: 4px !important;
  }

  .ant-select-selection-overflow {
    align-items: center !important;
    flex-wrap: wrap !important;
    max-height: 78px; /* ~2 tag rows (35px) + gaps */
    overflow: hidden;
  }

  .ant-select-selection-overflow-item-suffix {
    display: flex !important;
    align-items: center !important;
  }

  .ant-select-selection-search {
    margin-inline-start: 0 !important;
    display: flex !important;
    align-items: center !important;
  }

  .ant-select-selection-placeholder {
    inset-inline-start: 11px !important;
    top: 50% !important;
    transform: translateY(-50%) !important;
    line-height: 1.2 !important;
  }
`;
interface ChooseReturnProps {
  editMode: boolean;
  setSectionTouched: (value: boolean) => void;
  groupContext?: { fromGroup?: boolean; groupId?: string };
}

interface Country {
  countryId: string;
  countryName: string;
}

type FieldType = {
  country?: string;
  returnType?: string;
  ossCountry?: string[];
  returnFromDate?: Date;
  returnDueDate?: Date;
  returnFrequency?: ReturnFrequency;
  nextPeriod?: string;
};

enum FieldNames {
  COUNTRY = 'country',
  RETURN_TYPE = 'returnType',
  OSS_COUNTRY = 'ossCountry',
  FREQUENCY = 'returnFrequency',
  FROM_DATE = 'returnFromDate',
  DUE_DATE = 'returnDueDate',
}

const DEFAULT_DUE_DATE_OFFSET_DAYS = 10;
const OSS_RETURN_TYPE_ID = (process.env.OSS_ID || '').trim();

export const ChooseReturn: React.FC<ChooseReturnProps> = ({
  editMode,
  setSectionTouched,
  groupContext,
}) => {
  const { t, i18n } = useTranslation();
  const { localizeCountry } = useCountryLabel();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const [endDate, setEndDate] = useState<Date>(new Date('1900-01-01'));
  const [countries, setCountries] = useState<Country[]>([]);
  const [returnTypes, setReturnTypes] = useState<ReturnType[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string>();
  const [selectedType, setSelectedType] = useState<string>();
  const [selectedFrequency, setSelectedFrequency] = useState<ReturnFrequency>();
  const [selectedStartDate, setSelectedStartDate] = useState<any>();
  const [selectedSubmissionDate, setSelectedSubmissionDate] = useState<any>();
  const [nextPeriod, setNextPeriod] = useState<string>();
  const mappingData = useSelector(getMappingData);
  const returnData = useSelector(getReturnData);
  const preLoading = useSelector(getReturnPreLoading);
  const taxRatesLoading = useSelector(getReturnTaxRatesLoading);
  const loading = useSelector(getReturnLoading);
  const currentUser = useSelector(getAuthUser);
  const currentSubscription = useSelector(getCurrentSubscription);
  const subscriptionPlanTypes = useSelector(getSubscriptionPlanTypes);
  const userLicence = useSelector(getAuthUserLicence);
  const returnFrequency = returnData?.returnFrequency;

  const lockOss = useMemo(
    () =>
      isSmallOrMediumPlanLocked(
        currentSubscription,
        userLicence,
        subscriptionPlanTypes
      ),
    [subscriptionPlanTypes, currentSubscription, userLicence]
  );

  const handleOssUpgradeClick = useCallback(() => {
    const modalData: ModalData = {
      title: '',
      type: WindowType.WARNING,
      component: (
        <UpgradeMessage
          source={UpgradeMessageSource.OSS_RETURN}
          onClose={() => dispatch(turnModalOff())}
          userAccountType={currentUser?.accountType}
        />
      ),
      maskClosable: false,
    };
    dispatch(turnModalOn(modalData));
  }, [currentUser?.accountType, dispatch]);

  const onSetEndDate = (endDate: Date) => {
    setEndDate(endDate);
  };

  const onFrequencyChange = (frequency: ReturnFrequency) => {
    form.validateFields([[FieldNames.FREQUENCY]]);
    if (selectedSubmissionDate) {
      form.resetFields(['returnDueDate']);
      setSelectedSubmissionDate(undefined);
    }

    setSelectedFrequency(frequency);
  };

  const onStartDateChange = (date: any) => {
    form.validateFields([[FieldNames.FROM_DATE]]);

    if (selectedSubmissionDate) {
      form.resetFields(['returnDueDate']);
      setSelectedSubmissionDate(undefined);
    }
    setSelectedStartDate(date);
  };
  const onSubmissionDateChange = (date: any) => {
    form.validateFields([[FieldNames.DUE_DATE]]);
    setSelectedSubmissionDate(date);
  };

  useEffect(() => {
    let retType;
    if (mappingData?.mappingId) retType = returnData?.returnTypesEdit;
    else retType = returnData?.returnTypes;
    if (retType) {
      const cntrs: Country[] = [];
      retType.forEach((rt: ReturnType) => {
        rt.countryId &&
          cntrs.findIndex((c) => c.countryId === rt.countryId) === -1 &&
          cntrs.push({ countryId: rt.countryId, countryName: rt.countryName });
      });
      cntrs?.sort((a, b) => {
        if (a.countryName > b.countryName) {
          return 1;
        }

        if (a.countryName < b.countryName) {
          return -1;
        }

        return 0;
      });
      setCountries(cntrs);
    }
  }, [returnData]);

  const onCountryChange = (countryId?: string) => {
    const retTypes = mappingData?.mappingId
      ? returnData?.returnTypesEdit
      : returnData?.returnTypes;
    if (retTypes) {
      const newReturnTypes: ReturnType[] = retTypes.filter(
        (rt) => rt.countryId === countryId
      );
      form.validateFields([[FieldNames.COUNTRY]]);
      setSelectedCountry(countryId);
      setSelectedType(undefined);
      form.setFieldsValue({
        ['returnType']: undefined,
        [FieldNames.OSS_COUNTRY]: undefined,
      });
      setReturnTypes(newReturnTypes);
    }
  };

  useEffect(() => {
    if (selectedStartDate && selectedFrequency) {
      const nextPeriod: any[] = calculateNextPeriod(
        selectedStartDate,
        selectedFrequency
      );
      const dEndDate = new Date(nextPeriod[1]);
      onSetEndDate(dEndDate);
      setTimeout(function () {}, 1000);
      setNextPeriod(nextPeriod[0]);

      const defaultDueDate = suggestFirstSubmissionDeadlineAfterPeriodEnd(
        nextPeriod[1]
      );
      setSelectedSubmissionDate(defaultDueDate);
      form.setFieldsValue({
        [FieldNames.DUE_DATE]: defaultDueDate,
      });
    }
  }, [selectedFrequency, selectedStartDate]);

  const dueDateDisabled = () =>
    endDate.valueOf() === new Date('1900-01-01').valueOf();

  const disabledDate: RangePickerProps['disabledDate'] = (current) => {
    if (!current) return false;
    // Compare by local calendar day to avoid timezone shifts skipping the first valid day.
    const minAllowedDate = dayjs(endDate).add(1, 'day').startOf('day');
    return current.startOf('day').isBefore(minAllowedDate);
  };

  const requestedSectionsTypeId = useRef<string>();

  useEffect(() => {
    const connectionId = returnData?.connectionId;
    if (!selectedType || !connectionId || loading || preLoading) return;
    if (returnData?.sectionsReturnTypeId === selectedType) return;
    const requestKey = `${connectionId}:${selectedType}`;
    if (requestedSectionsTypeId.current === requestKey) return;
    requestedSectionsTypeId.current = requestKey;

    dispatch(fetchAddReturnDataRequest(connectionId, returnData, selectedType));
  }, [dispatch, loading, preLoading, returnData, selectedType]);

  const yearlyFrequency = 'YEARLY' as ReturnFrequency;

  const onReturnTypeChange = (returnType?: string) => {
    const selectedType = returnTypes.find((rt) => rt.id === returnType);
    //sortSelectionByReturnType(returnType);
    form.validateFields([[FieldNames.RETURN_TYPE]]);
    setSelectedType(selectedType?.id);
    form.setFieldsValue({
      [FieldNames.OSS_COUNTRY]: undefined,
    });
    if (isMaltaTifdReturnType(selectedType?.id)) {
      setSelectedFrequency(yearlyFrequency);
      form.setFieldsValue({
        [FieldNames.FREQUENCY]: yearlyFrequency,
      });
    }
    if (
      lockOss &&
      selectedType?.id &&
      selectedType.id === OSS_RETURN_TYPE_ID
    ) {
      handleOssUpgradeClick();
    }
  };
  const shouldShowOssCountry =
    !!selectedType && selectedType === OSS_RETURN_TYPE_ID;
  const isOssLocked = lockOss && shouldShowOssCountry;
  const ossCountryOptions = React.useMemo(() => {
    if (!selectedType || !returnData?.sections) return [];

    return returnData.sections
      .filter((section) => section.returnTypeId === selectedType)
      .filter((section) => !isOssOutOfScopeSectionName(section.name))
      .map((section) => ({
        value: section.id,
        label: section.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [returnData?.sections, selectedType]);

  const ossCountrySelectRef = useRef<unknown>(null);
  const selectedOssCountryIds =
    (Form.useWatch(FieldNames.OSS_COUNTRY, form) as string[] | undefined) || [];
  const selectedOssCountryLabels = useMemo(() => {
    const labelById = new Map(
      ossCountryOptions.map((option) => [option.value, option.label])
    );
    return selectedOssCountryIds.map(
      (id) => labelById.get(id) || id
    );
  }, [ossCountryOptions, selectedOssCountryIds]);
  const ossCountryMaxTagCount = useMultiLineMaxTagCount(
    ossCountrySelectRef,
    selectedOssCountryLabels,
    2
  );
  const ossCountryMaxTagPlaceholder = useCallback(
    (omittedValues: { label?: React.ReactNode }[]) => (
      <StyledTooltip //@ts-ignore
        styles={{
          root: {
            pointerEvents: 'none',
          },
        }}
        title={
          <>
            {omittedValues.map(({ label }, i) => (
              <React.Fragment key={`${String(label)}-${i}`}>
                <span>{label}</span>
                <br />
              </React.Fragment>
            ))}
          </>
        }
      >
        <span>
          +{formatDisplayInteger(omittedValues.length, i18n.language)}
        </span>
      </StyledTooltip>
    ),
    [i18n.language]
  );

  const handleSubmit = () => {
    if (isOssLocked) {
      handleOssUpgradeClick();
      return;
    }
    const mappingId =
      mappingData?.mappingId && !editMode ? undefined : mappingData?.mappingId;
    const jsonMapping =
      mappingData?.mappingId && !editMode
        ? undefined
        : mappingData?.jsonMapping;
    const typeId = selectedType;
    const returnFrequency = selectedFrequency;
    const countryId = selectedCountry;
    const ossCountryIds = (form.getFieldValue(
      FieldNames.OSS_COUNTRY
    ) || []) as string[];
    const periodFromDate = moment(new Date(selectedStartDate)).format(
      'YYYY-MM-DD'
    );

    const returnDueDate = moment(new Date(selectedSubmissionDate)).format(
      'YYYY-MM-DD'
    );
    let typeChanged = false;
    const prevType = mappingData?.typeId;
    if (editMode && prevType !== typeId) typeChanged = true;

    const selectedReturnType =
      returnTypes.find((rt) => rt.id === typeId) ??
      (editMode ? returnData?.returnTypesEdit : returnData?.returnTypes)?.find(
        (rt) => rt.id === typeId
      ) ??
      (editMode ? returnData?.returnTypes : returnData?.returnTypesEdit)?.find(
        (rt) => rt.id === typeId
      );

    const md: MappingData = {
      ...mappingData,
      typeId,
      typeName: selectedReturnType?.name ?? mappingData?.typeName,
      countryName: selectedReturnType?.countryName ?? mappingData?.countryName,
      periodFromDate,
      returnDueDate,
      returnFrequency,
      countryId,
      ossCountryIds,
      mappingId,
      jsonMapping,
      typeChanged,
    };
    dispatch(updateMappingData(md));

    if (editMode && mappingData?.mappingId) {
      setSectionTouched(true);
      dispatch(turnDrawerOff());
    } else if (returnData?.connectionId) {
      dispatch(turnDrawerOff());
      const baseRoute = isMaltaTifdReturnType(typeId)
        ? maltaTifdMappingPath(returnData.connectionId)
        : routes.returns.add.replace(
            ':connectionId',
            returnData.connectionId
          );
      const search = buildGroupReturnContextSearch(groupContext);
      if (location.pathname !== baseRoute || location.search !== search) {
        navigate(`${baseRoute}${search}`);
      }
    }
  };
  useEffect(() => {
    if (mappingData && editMode) {
      const nextPeriod: any = calculateNextPeriod(
        mappingData.periodFromDate,
        mappingData.returnFrequency ? mappingData.returnFrequency : ''
      );

      setEndDate(new Date(nextPeriod[1]));
      onCountryChange(mappingData.countryId);

      setSelectedType(mappingData.typeId);
      setSelectedFrequency(mappingData?.returnFrequency);
      setSelectedStartDate(dayjs(mappingData?.periodFromDate));
      setSelectedSubmissionDate(dayjs(mappingData?.returnDueDate));
      form.setFieldsValue({
        ['country']: mappingData.countryId,
        ['returnType']: mappingData.typeId,
        [FieldNames.OSS_COUNTRY]: mappingData.ossCountryIds || [],
        ['returnFrequency']: mappingData.returnFrequency
          ? mappingData.returnFrequency
          : '',
        ['returnFromDate']: dayjs(mappingData?.periodFromDate),
        ['nextPeriod']: nextPeriod[0],
        ['returnDueDate']: dayjs(mappingData?.returnDueDate),
      });
    }
  }, [mappingData]);

  useEffect(() => {
    if (editMode || !mappingData) return;
    if (!mappingData.countryId || !mappingData.typeId) return;

    const retTypes = returnData?.returnTypes || [];
    const selectedReturnType = retTypes.find(
      (rt) => rt.id === mappingData.typeId
    );
    if (!selectedReturnType) return;

    onCountryChange(mappingData.countryId);
    setSelectedType(mappingData.typeId);
    setSelectedFrequency(mappingData.returnFrequency);

    const startDate = mappingData.periodFromDate
      ? dayjs(mappingData.periodFromDate)
      : undefined;
    const dueDate = mappingData.returnDueDate
      ? dayjs(mappingData.returnDueDate)
      : undefined;

    if (startDate) setSelectedStartDate(startDate);
    if (dueDate) setSelectedSubmissionDate(dueDate);

    const calculatedNextPeriod =
      mappingData.periodFromDate && mappingData.returnFrequency
        ? calculateNextPeriod(
            mappingData.periodFromDate,
            mappingData.returnFrequency
          )[0]
        : undefined;

    form.setFieldsValue({
      ['country']: mappingData.countryId,
      ['returnType']: mappingData.typeId,
      [FieldNames.OSS_COUNTRY]: mappingData.ossCountryIds || [],
      ['returnFrequency']: mappingData.returnFrequency || undefined,
      ['returnFromDate']: startDate,
      ['nextPeriod']: calculatedNextPeriod,
      ['returnDueDate']: dueDate,
    });
  }, [editMode, mappingData, returnData]);

  return (
    <Spin spinning={!!preLoading}>
      <div className="mapping-form-root">
      <Row justify={'center'}>
        <Col span={24}>
          <Form
            name="basic"
            form={form}
            autoComplete="off"
            onFinish={handleSubmit}
          >
            <LabelWrapper>
              <span className="label-text">{t('common.country')}</span>
            </LabelWrapper>
            <Form.Item<FieldType>
              name="country"
              rules={[
                {
                  message: t('common.required-field'),
                  validator: () => {
                    if (!selectedCountry) {
                      return Promise.reject('Message problem');
                    } else {
                      return Promise.resolve();
                    }
                  },
                },
              ]}
            >
              <FormSelect //@ts-ignore
                width="100%"
                height="45px"
                placeholder={t('common.select')}
                onChange={onCountryChange}
                value={selectedCountry}
                disabled={mappingData && !!mappingData.mappingId && editMode}
                bordercolor="var(--color-border, #dbeae3)"
                bgcolor="var(--color-surface-raised, #FFFFFF)"
              >
                {countries &&
                  countries.map((c) => (
                    <Select.Option key={c.countryId} value={c.countryId}>
                      {localizeCountry(c.countryName, c.countryName)}
                    </Select.Option>
                  ))}
              </FormSelect>
            </Form.Item>
            <LabelWrapper>
              <span className="label-text">
                {t('returns.mapping.tax-return')}
              </span>
            </LabelWrapper>
            <Form.Item<FieldType>
              name="returnType"
              rules={[
                {
                  message: t('common.required-field'),
                  validator: () => {
                    if (!selectedType) {
                      return Promise.reject('Message problem');
                    } else {
                      return Promise.resolve();
                    }
                  },
                },
              ]}
            >
              <FormSelect
                placeholder={t('common.select')}
                onChange={onReturnTypeChange}
                disabled={mappingData && !!mappingData.mappingId && editMode} //@ts-ignore
                width="100%"
                height="45px"
                bordercolor="var(--color-border, #dbeae3)"
                bgcolor="var(--color-surface-raised, #FFFFFF)"
              >
                {returnTypes?.map((rt) => {
                  const isLockedOssOption =
                    lockOss && rt.id === OSS_RETURN_TYPE_ID;
                  return (
                    <Select.Option key={rt.id} value={rt.id}>
                      <span
                        key={rt.id}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          ...(editMode
                            ? { color: 'var(--color-text-base, #2e2e2e)' }
                            : {}),
                        }}
                      >
                        {isLockedOssOption && <LockFilled />}
                        {getReturnTypeNameLabel(
                          rt.name,
                          t,
                          i18n.language,
                          rt.id
                        )}
                      </span>
                    </Select.Option>
                  );
                })}
              </FormSelect>
            </Form.Item>
            {selectedType && !shouldShowOssCountry && (
              <DescriptionBox>
                <strong>
                  {`${getReturnTypeNameLabel(
                    returnTypes.find((rt) => rt.id === selectedType)?.name ?? '',
                    t,
                    i18n.language,
                    selectedType
                  ).trim()}: `}
                </strong>
                {returnTypes.find((rt) => rt.id === selectedType)?.description}
              </DescriptionBox>
            )}
            {shouldShowOssCountry && (
              <Form.Item<FieldType>
                name={FieldNames.OSS_COUNTRY}
                style={{ marginTop: -18, marginBottom: 20 }}
              >
                <OssCountrySelect
                  ref={ossCountrySelectRef}
                  placeholder={t('returns.mapping.select-oss-countries')} //@ts-ignore
                  mode="multiple"
                  width="100%"
                  height="45px"
                  bordercolor="var(--color-border, #dbeae3)"
                  bgcolor="var(--color-surface-raised, #FFFFFF)"
                  maxTagCount={ossCountryMaxTagCount}
                  maxTagPlaceholder={ossCountryMaxTagPlaceholder}
                  showSearch
                  optionFilterProp="children"
                  disabled={isOssLocked}
                >
                  {ossCountryOptions.map((option) => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </OssCountrySelect>
              </Form.Item>
            )}
            <LabelWrapper>
              <span className="label-text">
                {t('returns.mapping.return-frequency')}
              </span>
            </LabelWrapper>
            <Form.Item<FieldType>
              name="returnFrequency"
              rules={[
                {
                  message: t('common.required-field'),
                  validator: () => {
                    if (!selectedFrequency) {
                      return Promise.reject('Message problem');
                    } else {
                      return Promise.resolve();
                    }
                  },
                },
              ]}
            >
              <FormSelect
                placeholder={t('common.select')}
                onChange={onFrequencyChange} //@ts-ignore
                width="100%"
                height="45px"
                bordercolor="var(--color-border, #dbeae3)"
                bgcolor="var(--color-surface-raised, #FFFFFF)"
                disabled={isOssLocked}
              >
                {returnFrequency &&
                  Object.keys(returnFrequency).map((rf) => (
                    <Select.Option key={rf} value={rf}>
                      {getReturnFrequencyLabel(rf, t)}
                    </Select.Option>
                  ))}
              </FormSelect>
            </Form.Item>
            <LabelWrapper>
              <span className="label-text">
                {t('returns.mapping.return-start-date')}
              </span>
            </LabelWrapper>
            <Form.Item<FieldType>
              name="returnFromDate"
              rules={[
                {
                  message: t('common.required-field'),
                  validator: () => {
                    if (!selectedStartDate) {
                      return Promise.reject('Message problem');
                    } else {
                      return Promise.resolve();
                    }
                  },
                },
              ]}
            >
              <FormDatePicker
                style={{ width: '100%' }}
                placeholder={t('common.select')}
                onChange={onStartDateChange}
                format="DD/MM/YYYY" //@ts-ignore
                hasValue={!!selectedStartDate}
                disabled={isOssLocked}
              />
            </Form.Item>
            <LabelWrapper>
              <span className="label-text">
                {t('returns.mapping.period-chosen')}
              </span>
            </LabelWrapper>
            <Form.Item<FieldType> name="nextPeriod">
              <PeriodBox
                style={
                  isOssLocked
                    ? { opacity: 0.55, pointerEvents: 'none' }
                    : undefined
                }
              >
                {nextPeriod}
              </PeriodBox>
            </Form.Item>
            <LabelWrapper>
              <span className="label-text">
                {t('returns.mapping.return-due-date')}
              </span>
            </LabelWrapper>
            <Form.Item<FieldType>
              name="returnDueDate"
              rules={[
                {
                  message: t('common.required-field'),
                  validator: () => {
                    if (!selectedSubmissionDate) {
                      return Promise.reject('Message problem');
                    } else {
                      return Promise.resolve();
                    }
                  },
                },
              ]}
            >
              <FormDatePicker
                style={{ width: '100%' }}
                placeholder={t('common.select')}
                format="DD/MM/YYYY"
                onChange={onSubmissionDateChange}
                disabledDate={disabledDate}
                disabled={isOssLocked || dueDateDisabled()} //@ts-ignore
                hasValue={!!selectedSubmissionDate}
              />
            </Form.Item>
            <Form.Item>
              <ScopeButton
                type="primary"
                htmlType="submit"
                loading={taxRatesLoading || loading} //@ts-ignore
                width="100%"
                disabled={isOssLocked}
              >
                {editMode && mappingData?.mappingId
                  ? t('common.update')
                  : t('common.continue')}
              </ScopeButton>
            </Form.Item>
            <NoteText>
              <strong>{t('common.note')}:</strong>
              {t('other.choose-return-note')}
            </NoteText>
          </Form>
        </Col>
      </Row>
      </div>
    </Spin>
  );
};

